import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    signal,
    ViewChild,
    WritableSignal
} from '@angular/core';
import Player from '@vimeo/player';
import { UmbracoImage } from '@models/umbraco.model';
import { FormatStringPipe } from '@shared/format-string/format-string.pipe';

import { AsyncPipe, NgClass, NgOptimizedImage } from '@angular/common';
import { SafePipe } from '@shared/safe/safe.pipe';
import { SvgIconComponent } from '@shared/svg-icon/svg-icon.component';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateSafePipe } from '@shared/translate-safe/translate-safe.pipe';
import { Subject } from 'rxjs';
import { GetVideoThumbnailPipe } from '@features/video-player/pipes/get-video-thumbnail.pipe';
import { CookieConsentService } from '@core/services/cookie-consent/cookie-consent.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { VideoPlayerService } from '@features/video-player/video-player.service';
import { LoadingDotsComponent } from '@shared/loading-dots/loading-dots.component';
import { debounceTime, filter, take, takeUntil } from 'rxjs/operators';
import { IntersectionService } from '@core/services/intersection.service';
import { VideoPlayerError, VideoPlayerState, VideoPlayerStatus } from '@features/video-player/video.models';

@UntilDestroy()
@Component({
    selector: 'app-vimeo-player',
    imports: [
    FormatStringPipe,
    SafePipe,
    SvgIconComponent,
    TranslateModule,
    TranslateSafePipe,
    GetVideoThumbnailPipe,
    NgClass,
    NgOptimizedImage,
    AsyncPipe,
    LoadingDotsComponent
],
    providers: [VideoPlayerService],
    templateUrl: './vimeo-player.component.html',
    styleUrls: ['../video-player.shared.scss', './vimeo-player.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VimeoPlayerComponent implements AfterViewInit, OnDestroy {
    private readonly unsubscribe: Subject<void> = new Subject();

    private intersectionKey: string = 'vimeo-video-player';
    private intersectionOptions: IntersectionObserverInit = {
        threshold: 0.5
    };
    private playerIsReady = false;
    private triedToPlay = false;

    public playerState: WritableSignal<VideoPlayerState> = signal(VideoPlayerState.Idle);
    public showVideoFrame = false;
    public loadIFrame = false;
    public player?: Player;
    public videoError?: string;
    public showConsentDisclaimer: boolean = false;

    @ViewChild('vimeoPlayer') private vimeoPlayer: ElementRef | undefined;

    @Input() public videoId?: string;
    @Input() public thumbnail: UmbracoImage;
    @Input() public caption?: string;
    @Input() public autoplay = false;
    @Input() public loop = false;
    @Input() public controls = true;
    @Input() public muted = false;

    constructor(
        private readonly element: ElementRef<HTMLElement>,
        private readonly intersectionService: IntersectionService,
        private readonly cd: ChangeDetectorRef,
        public cookieConsentService: CookieConsentService
    ) {}

    ngAfterViewInit(): void {
        this.cookieConsentService.consentStatus$.pipe(untilDestroyed(this)).subscribe(data => {
            this.showConsentDisclaimer = false;

            if (!data.cookie_cat_statistic) {
                this.showConsentDisclaimer = true;
                return;
            }

            this.checkIntersection();
        });
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();

        if (this.player) {
            this.player.off('error', this.onStateUpdate.bind(this, VideoPlayerState.Error));
            this.player.off('play', this.onStateUpdate.bind(this, VideoPlayerState.Playing));
            this.player.off('pause', this.onStateUpdate.bind(this, VideoPlayerState.Paused));
            this.player.off('ended', this.onStateUpdate.bind(this, VideoPlayerState.Ended));
            this.player.off('bufferstart', this.onStateUpdate.bind(this, VideoPlayerState.Buffering));

            this.player.destroy();
            this.playerIsReady = false;
        }
    }

    public loadVideoFrame(play: boolean): void {
        this.cookieConsentService.consentStatus$.pipe(take(1), untilDestroyed(this)).subscribe(status => {
            if (status.cookie_cat_statistic) {
                this.createPlayer(play ?? this.autoplay);
            } else {
                this.showConsentDisclaimer = true;
            }
        });
    }

    private checkIntersection(): void {
        this.intersectionService
            .observe(this.intersectionKey, this.element.nativeElement, this.intersectionOptions)
            .pipe(
                // i added a debounceTime because if we scroll right to the end of the page
                // will trigger the video right away if we have autoplay on;
                debounceTime(100),
                takeUntil(this.unsubscribe),
                filter((entry: IntersectionObserverEntry) => entry.target === this.element.nativeElement)
            )
            .subscribe(entry => {
                this.intersectionControlPlayer(entry);
            });
    }

    private createPlayer(play: boolean = false): void {
        if (!this.vimeoPlayer) {
            console.warn('No player element found');
        }

        if (!this.videoId) {
            console.warn('No video id found');
        }

        if (this.vimeoPlayer && this.videoId) {
            this.player = new Player(this.vimeoPlayer.nativeElement, {
                id: parseInt(this.videoId, 10),
                autoplay: play,
                controls: this.controls,
                loop: this.loop,
                muted: this.autoplay ? true : this.muted
            });

            this.playerIsReady = true;
            this.player.on('error', this.onStateUpdate.bind(this, VideoPlayerState.Error));
            this.player.on('play', this.onStateUpdate.bind(this, VideoPlayerState.Playing));
            this.player.on('pause', this.onStateUpdate.bind(this, VideoPlayerState.Paused));
            this.player.on('ended', this.onStateUpdate.bind(this, VideoPlayerState.Ended));
            this.player.on('bufferstart', this.onStateUpdate.bind(this, VideoPlayerState.Buffering));
            this.loadIFrame = true;
            this.showVideoFrame = true;
        }
    }

    private intersectionControlPlayer(entry: IntersectionObserverEntry): void {
        if (this.player && this.playerIsReady) {
            if (
                !entry.isIntersecting &&
                (this.playerState() === VideoPlayerState.Playing || this.playerState() === VideoPlayerState.Buffering)
            ) {
                this.player.pause();
                return;
            }

            if (entry.isIntersecting && this.autoplay) {
                this.player.play().catch(this.tryToPlay.bind(this));
                return;
            }

            return;
        }

        if (entry.isIntersecting && this.autoplay && !this.playerIsReady) {
            this.loadVideoFrame(this.autoplay);
        }
    }

    private onStateUpdate(state: VideoPlayerState, info: VideoPlayerError | VideoPlayerStatus): void {
        this.playerState.set(state);

        if (state === VideoPlayerState.Error) {
            this.videoError = 'message' in info ? info.message : 'An error occurred while playing the video';
        }

        this.cd.detectChanges();
    }

    /**
     * Let's try to play the video again if the user never interacted with the page,
     * let's mute the video and try to play again; if it fails again we will show an error message;
     * @param info
     * @private
     */
    private tryToPlay(info: VideoPlayerError | VideoPlayerStatus): void {
        if (!('name' in info) && !('message' in info)) {
            return;
        }

        if (this.triedToPlay) {
            this.videoError = info?.message?.toString() ?? 'An error occurred while playing the video';

            return;
        }

        // if for some reason the video is not allowed to play because the user never interacted with the page
        // we need to mute the video and try to play again;
        if ('name' in info && info.name === 'NotAllowedError') {
            this.player?.setMuted(true);
            this.element.nativeElement.focus();
            this.player?.play();
            console.warn('Trying to play the video again');
        }

        this.triedToPlay = true;
    }
}
